import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Check, X, GripVertical, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const ExtrasManager = () => {
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    price_cents: 0,
    active: true,
    display_order: 0
  });

  useEffect(() => {
    loadExtras();
  }, []);

  const loadExtras = async () => {
    try {
      setLoading(true);
      const response = await api.get('/extras/all');
      setExtras(response.data.extras || []);
    } catch (error) {
      toast.error('Error al cargar extras');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingExtra(null);
    setForm({
      name: '',
      description: '',
      price_cents: 0,
      active: true,
      display_order: extras.length
    });
    setShowDialog(true);
  };

  const openEditDialog = (extra) => {
    setEditingExtra(extra);
    setForm({
      name: extra.name,
      description: extra.description || '',
      price_cents: extra.price_cents,
      active: extra.active,
      display_order: extra.display_order
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    
    if (form.price_cents < 0) {
      toast.error('El precio no puede ser negativo');
      return;
    }
    
    setSaving(true);
    try {
      if (editingExtra) {
        await api.put(`/extras/${editingExtra.id}`, form);
        toast.success('Extra actualizado');
      } else {
        await api.post('/extras', form);
        toast.success('Extra creado');
      }
      setShowDialog(false);
      loadExtras();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (extra) => {
    try {
      await api.patch(`/extras/${extra.id}/toggle`);
      toast.success(extra.active ? 'Extra desactivado' : 'Extra activado');
      loadExtras();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (extra) => {
    if (!window.confirm(`¿Eliminar "${extra.name}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await api.delete(`/extras/${extra.id}`);
      toast.success('Extra eliminado');
      loadExtras();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toFixed(2) + '€';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Extras</h2>
          <p className="text-zinc-400 text-sm">Gestiona los servicios adicionales para las reservas</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="create-extra-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Extra
        </Button>
      </div>

      {/* Extras List */}
      {extras.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <p className="text-zinc-400 mb-4">No hay extras creados</p>
          <Button onClick={openCreateDialog} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Crear primer extra
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {extras.map((extra) => (
            <div 
              key={extra.id}
              className={`bg-zinc-900/50 rounded-lg border p-4 ${
                extra.active ? 'border-zinc-700' : 'border-zinc-800 opacity-60'
              }`}
              data-testid={`extra-item-${extra.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 bg-zinc-800 rounded-lg mt-1">
                    <GripVertical className="w-4 h-4 text-zinc-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{extra.name}</h3>
                      {!extra.active && (
                        <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-0.5 rounded">
                          Inactivo
                        </span>
                      )}
                    </div>
                    {extra.description && (
                      <p className="text-sm text-zinc-400 mt-1">{extra.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-lg font-bold text-primary">
                        {formatPrice(extra.price_cents)}
                      </span>
                      <span className="text-xs text-zinc-500">
                        Orden: {extra.display_order}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={extra.active}
                    onCheckedChange={() => handleToggle(extra)}
                    data-testid={`toggle-extra-${extra.id}`}
                  />
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openEditDialog(extra)}
                    data-testid={`edit-extra-${extra.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(extra)}
                    className="text-red-500 hover:text-red-400"
                    data-testid={`delete-extra-${extra.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingExtra ? 'Editar Extra' : 'Nuevo Extra'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white">Nombre *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Ej: Limpieza interior"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="extra-name-input"
                required
              />
            </div>
            
            <div>
              <Label className="text-white">Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Descripción del servicio..."
                className="bg-zinc-800 border-zinc-700 text-white"
                rows={3}
                data-testid="extra-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Precio (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={(form.price_cents / 100).toFixed(2)}
                  onChange={(e) => setForm({...form, price_cents: Math.round(parseFloat(e.target.value || 0) * 100)})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="extra-price-input"
                  required
                />
              </div>
              <div>
                <Label className="text-white">Orden</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.display_order}
                  onChange={(e) => setForm({...form, display_order: parseInt(e.target.value || 0)})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="extra-order-input"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Switch
                checked={form.active}
                onCheckedChange={(checked) => setForm({...form, active: checked})}
                data-testid="extra-active-switch"
              />
              <Label className="text-white">Activo</Label>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowDialog(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={saving}
                data-testid="save-extra-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingExtra ? 'Guardar cambios' : 'Crear extra'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExtrasManager;
